import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getSessionItem,
  setSessionItem,
} from "../helpers/function.helper";
import enTranslations from "../i18n/en";
import teTranslations from "../i18n/te";
import type { SupportedLanguage } from "../types/language";
export type { SupportedLanguage } from "../types/language";

type TranslationMap = Record<string, string>;

interface LanguageContextValue {
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
  t: (key: string) => string;
  isLoaded: boolean;
}

const translations: Record<SupportedLanguage, TranslationMap> = {
  en: enTranslations,
  te: teTranslations,
};

const LanguageContext = createContext<LanguageContextValue | undefined>(
  undefined
);

export const LanguageProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>("en");
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const stored = await getSessionItem("activeLang");
        if (!isMounted) return;
        if (stored === "te" || stored === "en") {
          setLanguageState(stored);
        }
      } finally {
        if (isMounted) {
          setIsLoaded(true);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    setLanguageState(lang);
    void setSessionItem("activeLang", lang);
  }, []);

  const t = useCallback(
    (key: string) => {
      const currentDictionary = translations[language] ?? translations.en;
      if (currentDictionary[key]) {
        return currentDictionary[key];
      }
      if (translations.en[key]) {
        return translations.en[key];
      }
      return key;
    },
    [language]
  );

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      setLanguage,
      t,
      isLoaded,
    }),
    [language, setLanguage, t, isLoaded]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LANG_LABELS: Record<SupportedLanguage, string> = {
  en: translations.en["navbar.language.english"],
  te: translations.te["navbar.language.telugu"],
};
